import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { BookMarked } from "lucide-react";
import api from "@/lib/api";

export function useVerseCommentaries(book, chapter, verse) {
  return useQuery({
    queryKey: ["verse-commentaries", book, chapter, verse],
    queryFn: async () => (await api.get(`/commentaries/verse/${encodeURIComponent(book)}/${chapter}/${verse}`)).data.commentaries,
    enabled: !!book && !!chapter && !!verse,
  });
}

export function CommentaryChooser({ q, title, desc, activeSlug, onOpen }) {
  const { data: list } = useVerseCommentaries(q.book, q.chapter, q.verse);
  if (!list?.length) return null;
  const active = list.find((c) => c.slug === activeSlug);
  return (
    <section id="section-commentaries" className="qg-rise rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="section-commentaries">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2 className="font-serif text-2xl font-semibold text-foreground">{title}</h2>
        {desc && <span className="hidden font-mono text-[11px] uppercase tracking-wider text-muted-foreground sm:block">{desc}</span>}
      </div>
      <p className="text-[15px] leading-relaxed text-muted-foreground">Generations of believers have studied this verse. See what they noticed.</p>
      <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Choose a commentary">
        {list.map((c) => {
          const on = c.slug === activeSlug;
          return (
            <button key={c.slug} type="button" onClick={() => onOpen(c.slug)} aria-pressed={on} data-testid={`commentary-choose-${c.slug}`}
              className={`flex items-center gap-1.5 rounded-full border px-4 py-2 font-serif text-[17px] transition-colors duration-200 ${on ? "border-gold bg-gold text-white" : "border-border bg-background text-foreground hover:border-gold"}`}>
              <BookMarked className={`h-4 w-4 ${on ? "text-white" : "text-gold"}`} /> {c.lens_name}
            </button>
          );
        })}
      </div>
      <AnimatePresence mode="wait">
        {active && (
          <motion.div key={active.slug} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            className="mt-5 rounded-xl border-l-4 border-gold bg-gold/[0.06] px-4 py-3" data-testid="commentary-choose-summary">
            <p className="font-mono text-[11px] uppercase tracking-wider text-gold">{active.lens} · {active.year}</p>
            <p className="mt-1 text-sm leading-relaxed text-foreground/80">{active.lens_blurb}</p>
            <p className="mt-1 text-xs text-muted-foreground">Reading {active.lens_name} on {q.reference}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
